import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { API_ENDPOINTS, getApiUrl } from '../config/api';
import styles from '../styles/NavButton.module.css';
import addressStyles from '../styles/AddressAutocomplete.module.css';
import { getBrutalistStyles } from '../styles/brutalist';
import LocationSelector from '../components/auth/LocationSelector';
import FormStepper from '../components/auth/FormStepper';
import BrutalistNavigation from '../components/BrutalistNavigation';

const STEPS = [
  {
    id: 'info',
    title: 'Información de la Autoescuela',
    description: 'Datos básicos de la autoescuela'
  },
  {
    id: 'address',
    title: 'Dirección',
    description: 'Ubicación y datos de contacto'
  },
  {
    id: 'credentials',
    title: 'Credenciales',
    description: 'Configura el acceso a tu cuenta'
  }
];

// Password validation regex
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const RegistrarAutoescuela = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const brutalistStyles = getBrutalistStyles(isDarkMode);
  const [currentStep, setCurrentStep] = useState(() => {
    const savedStep = localStorage.getItem('registroAutoescuela_step');
    return savedStep ? parseInt(savedStep, 10) : 0;
  });

  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem('registroAutoescuela_data');
    return savedData ? JSON.parse(savedData) : {
      nombreAutoescuela: '',
      cifNif: '',
      email: '',
      telefono: '',
      direccionFiscal: '',
      complementoDireccion: '',
      provincia: '',
      ciudad: '',
      codigoPostal: '',
      password: '',
      confirmPassword: ''
    };
  });

  // Guardar estado en localStorage cuando cambie
  useEffect(() => {
    localStorage.setItem('registroAutoescuela_step', currentStep);
    localStorage.setItem('registroAutoescuela_data', JSON.stringify(formData));
  }, [currentStep, formData]);

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [mapsLoading, setMapsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const addressInputRef = useRef(null);

  // Handle responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Only initialize when on the address step
    if (currentStep !== 1) {
      setMapsLoading(false);
      return;
    }

    let autocomplete = null;
    let resizeObserver = null;
    let mapsLoadedListener = null;
    let timeoutId = null;
    let isInitialized = false;

    const updateDropdownPosition = () => {
      if (!addressInputRef.current) return;
      
      const input = addressInputRef.current;
      const rect = input.getBoundingClientRect();
      
      // Set CSS custom properties for positioning
      document.documentElement.style.setProperty('--autocomplete-width', `${rect.width}px`);
      document.documentElement.style.setProperty('--input-left', `${rect.left}px`);
      document.documentElement.style.setProperty('--input-top', `${rect.top}px`);
      document.documentElement.style.setProperty('--input-height', `${rect.height}px`);
    };

    const initAutocomplete = async () => {
      try {
        // Wait for Google Maps to be available
        if (!window.google?.maps?.places) {
          setMapsLoading(true);
          return new Promise((resolve, reject) => {
            const onMapsLoaded = () => {
              if (window.google?.maps?.places) {
                window.removeEventListener('google-maps-loaded', onMapsLoaded);
                resolve();
              }
            };

            mapsLoadedListener = onMapsLoaded;
            window.addEventListener('google-maps-loaded', onMapsLoaded);

            // Set a timeout
            timeoutId = setTimeout(() => {
              window.removeEventListener('google-maps-loaded', onMapsLoaded);
              reject(new Error('Tiempo de espera agotado'));
            }, 10000);
          });
        }

        // Verify address input is available
        if (!addressInputRef.current) {
          throw new Error('Campo de dirección no disponible');
        }

        autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
          types: ['address'],
          componentRestrictions: { country: 'es' },
          fields: ['address_components', 'formatted_address', 'name', 'geometry']
        });

        // Initialize position
        updateDropdownPosition();

        // Setup resize observer
        resizeObserver = new ResizeObserver(updateDropdownPosition);
        resizeObserver.observe(addressInputRef.current);

        // Handle window resize
        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition);

        // Add place_changed listener
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          
          if (!place.address_components) {
            console.warn('Place lacks address components');
            return;
          }

          let streetNumber = '';
          let route = '';
          let postalCode = '';
          let city = '';
          let province = '';
          let hasStreetNumber = false;
          
          place.address_components.forEach(component => {
            const types = component.types;
            if (types.includes('street_number')) {
              streetNumber = component.long_name;
              hasStreetNumber = true;
            }
            if (types.includes('route')) route = component.long_name;
            if (types.includes('postal_code')) postalCode = component.long_name;
            if (types.includes('locality')) city = component.long_name;
            if (!city && types.includes('administrative_area_level_3')) city = component.long_name;
            if (types.includes('administrative_area_level_2')) {
              province = component.long_name;
              if (!city) city = component.long_name;
            }
          });

          if (!postalCode) {
            const postalCodeMatch = place.formatted_address.match(/\b\d{5}\b/);
            if (postalCodeMatch) {
              postalCode = postalCodeMatch[0];
            }
          }

          setFormData(prev => ({
            ...prev,
            direccionFiscal: place.formatted_address || `${route} ${streetNumber}`.trim(),
            provincia: province,
            ciudad: city,
            codigoPostal: postalCode,
            codigoPostalWarning: (!hasStreetNumber && !postalCode) ?
              'Esta calle puede tener varios códigos postales. Por favor, añade el número o introduce el código postal manualmente.' :
              null
          }));

          if (addressInputRef.current) {
            addressInputRef.current.value = place.formatted_address;
          }
        });

        setMapsLoading(false);
      } catch (error) {
        console.error('Error initializing Places Autocomplete:', error);
        setErrors(prev => ({
          ...prev,
          direccionFiscal: 'Error al inicializar el autocompletado. Por favor, recarga la página.'
        }));
        setMapsLoading(false);
      }
    };

    const initialize = async () => {
      if (isInitialized) return;
      
      try {
        await initAutocomplete();
        isInitialized = true;
      } catch (error) {
        console.error('Error initializing Places Autocomplete:', error);
        setErrors(prev => ({
          ...prev,
          direccionFiscal: `Error al inicializar el autocompletado: ${error.message}`
        }));
      } finally {
        setMapsLoading(false);
      }
    };

    initialize();

    // Cleanup function
    return () => {
      // Clear timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Remove maps loaded listener
      if (mapsLoadedListener) {
        window.removeEventListener('google-maps-loaded', mapsLoadedListener);
      }

      // Clear autocomplete listeners
      if (autocomplete) {
        google.maps.event.clearInstanceListeners(autocomplete);
      }

      // Clear resize observer
      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      // Remove window event listeners
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition);

      // Clean up CSS custom properties
      [
        '--autocomplete-width',
        '--input-left',
        '--input-top',
        '--input-height'
      ].forEach(prop => {
        document.documentElement.style.removeProperty(prop);
      });

      // Reset states
      setMapsLoading(false);
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.direccionFiscal;
        return newErrors;
      });
      
      isInitialized = false;
    };
  }, [currentStep]); // Add currentStep to dependencies

  // Show loading indicator while Maps API is initializing
  useEffect(() => {
    if (mapsLoading) {
      setErrors(prev => ({
        ...prev,
        direccionFiscal: 'Cargando el servicio de direcciones...'
      }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.direccionFiscal === 'Cargando el servicio de direcciones...') {
          delete newErrors.direccionFiscal;
        }
        return newErrors;
      });
    }
  }, [mapsLoading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Clear warning when manually editing postal code
      ...(name === 'codigoPostal' ? { codigoPostalWarning: null } : {})
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleKeyDown = (e) => {
    // Prevent browser back navigation when pressing backspace
    if (e.key === 'Backspace' && e.target.value === '') {
      e.preventDefault();
    }
  };

  const handleLocationSelect = ({ province, city }) => {
    setFormData(prev => ({
      ...prev,
      provincia: province,
      ciudad: city
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Información básica
        if (!formData.nombreAutoescuela.trim()) {
          newErrors.nombreAutoescuela = 'El nombre es obligatorio';
        }
        const cifNifValue = formData.cifNif.toUpperCase();
        if (!cifNifValue.match(/^[A-Z0-9]{9}$/)) {
          newErrors.cifNif = 'Introduce un CIF/NIF válido (9 caracteres)';
        }
        if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
          newErrors.email = 'Email no válido';
        }
        if (!formData.telefono.match(/^[0-9]{9}$/)) {
          newErrors.telefono = 'Teléfono no válido (9 dígitos)';
        }
        break;

      case 1: // Dirección
        if (!formData.direccionFiscal.trim()) {
          newErrors.direccionFiscal = 'La dirección es obligatoria';
        }
        if (!formData.provincia) {
          newErrors.provincia = 'Selecciona una provincia';
        }
        if (!formData.ciudad.trim()) {
          newErrors.ciudad = 'La ciudad es obligatoria';
        }
        if (!formData.codigoPostal.match(/^[0-9]{5}$/)) {
          newErrors.codigoPostal = 'Código postal no válido (5 dígitos)';
        }
        break;

      case 2: // Credenciales
        if (!PASSWORD_REGEX.test(formData.password)) {
          newErrors.password = 'La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula, un número y un carácter especial';
        }
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Las contraseñas no coinciden';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(currentStep)) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.schoolRegister), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.nombreAutoescuela,
          email: formData.email,
          password: formData.password,
          nif: formData.cifNif.toUpperCase(),
          phone: formData.telefono,
          address: {
            street: formData.direccionFiscal,
            complement: formData.complementoDireccion,
            province: formData.provincia,
            city: formData.ciudad,
            postalCode: formData.codigoPostal
          }
        })
      });

      if (!response.ok) {
        let errorMessage = 'Error al registrar la autoescuela';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // If response is not JSON or is empty, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Limpiar localStorage al completar el registro
      localStorage.removeItem('registroAutoescuela_step');
      localStorage.removeItem('registroAutoescuela_data');
      
      // On success, redirect to login with success message
      navigate('/iniciar-sesion', {
        state: {
          message: 'Registro completado con éxito. Por favor, inicia sesión.'
        }
      });
    } catch (err) {
      setErrors({
        submit: err.message || 'Error al registrar la autoescuela. Por favor, inténtalo de nuevo.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label htmlFor="nombreAutoescuela" style={brutalistStyles.brutalistLabel}>
                Nombre de la Autoescuela
              </label>
              <input
                type="text"
                id="nombreAutoescuela"
                name="nombreAutoescuela"
                value={formData.nombreAutoescuela}
                onChange={handleChange}
                style={brutalistStyles.brutalistInput}
                placeholder="Nombre de tu autoescuela"
                required
              />
              {errors.nombreAutoescuela && (
                <span style={brutalistStyles.brutalistError}>{errors.nombreAutoescuela}</span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label htmlFor="cifNif" style={brutalistStyles.brutalistLabel}>
                CIF/NIF
              </label>
              <input
                type="text"
                id="cifNif"
                name="cifNif"
                value={formData.cifNif}
                onChange={handleChange}
                style={brutalistStyles.brutalistInput}
                placeholder="B12345678 o 12345678A"
                required
              />
              {errors.cifNif && (
                <span style={brutalistStyles.brutalistError}>{errors.cifNif}</span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label htmlFor="email" style={brutalistStyles.brutalistLabel}>
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={brutalistStyles.brutalistInput}
                placeholder="email@autoescuela.com"
                required
              />
              {errors.email && (
                <span style={brutalistStyles.brutalistError}>{errors.email}</span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label htmlFor="telefono" style={brutalistStyles.brutalistLabel}>
                Teléfono
              </label>
              <input
                type="tel"
                id="telefono"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                style={brutalistStyles.brutalistInput}
                placeholder="912345678"
                required
              />
              {errors.telefono && (
                <span style={brutalistStyles.brutalistError}>{errors.telefono}</span>
              )}
            </div>
          </div>
        );

      case 1:
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              position: 'relative',
              width: '100%'
            }}>
              <label htmlFor="direccionFiscal" style={brutalistStyles.brutalistLabel}>
                Dirección Fiscal
              </label>
              <div className={addressStyles.container} style={{ position: 'relative', width: '100%' }}>
                <input
                  ref={addressInputRef}
                  type="text"
                  id="direccionFiscal"
                  name="direccionFiscal"
                  value={formData.direccionFiscal}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  className={addressStyles.input}
                  style={{
                    backgroundColor: mapsLoading ? (isDarkMode ? '#333' : '#f5f5f5') : 'inherit',
                    cursor: mapsLoading ? 'not-allowed' : 'text'
                  }}
                  placeholder={mapsLoading ? "Cargando servicio de direcciones..." : "Empieza a escribir tu dirección..."}
                  required
                  autoComplete="off"
                  disabled={mapsLoading}
                />
              </div>
              {errors.direccionFiscal && (
                <span style={brutalistStyles.brutalistError}>{errors.direccionFiscal}</span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label htmlFor="complementoDireccion" style={brutalistStyles.brutalistLabel}>
                Complemento de Dirección
              </label>
              <input
                type="text"
                id="complementoDireccion"
                name="complementoDireccion"
                value={formData.complementoDireccion || ''}
                onChange={handleChange}
                style={brutalistStyles.brutalistInput}
                placeholder="Piso, puerta, local, etc..."
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '2', minWidth: '200px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label htmlFor="provincia" style={brutalistStyles.brutalistLabel}>
                    Provincia
                  </label>
                  <input
                    type="text"
                    id="provincia"
                    name="provincia"
                    value={formData.provincia}
                    style={brutalistStyles.brutalistInput}
                    readOnly
                    required
                  />
                  {errors.provincia && (
                    <span style={brutalistStyles.brutalistError}>{errors.provincia}</span>
                  )}
                </div>
              </div>

              <div style={{ flex: '2', minWidth: '200px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label htmlFor="ciudad" style={brutalistStyles.brutalistLabel}>
                    Ciudad
                  </label>
                  <input
                    type="text"
                    id="ciudad"
                    name="ciudad"
                    value={formData.ciudad}
                    style={brutalistStyles.brutalistInput}
                    onChange={handleChange}
                    required
                  />
                  {errors.ciudad && (
                    <span style={brutalistStyles.brutalistError}>{errors.ciudad}</span>
                  )}
                </div>
              </div>

              <div style={{ flex: '1', minWidth: '150px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label htmlFor="codigoPostal" style={brutalistStyles.brutalistLabel}>
                    Código Postal
                  </label>
                  <input
                    type="text"
                    id="codigoPostal"
                    name="codigoPostal"
                    value={formData.codigoPostal}
                    onChange={handleChange}
                    style={{
                      ...brutalistStyles.brutalistInput,
                      borderColor: formData.codigoPostalWarning ? '#ff8c00' : undefined
                    }}
                    required
                  />
                  {errors.codigoPostal && (
                    <span style={brutalistStyles.brutalistError}>{errors.codigoPostal}</span>
                  )}
                  {formData.codigoPostalWarning && (
                    <span style={{
                      color: '#ff8c00',
                      backgroundColor: '#fff3e6',
                      border: '2px solid #ff8c00',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      marginTop: '0.5rem',
                      display: 'block'
                    }}>
                      {formData.codigoPostalWarning}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label htmlFor="password" style={brutalistStyles.brutalistLabel}>
                Contraseña
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                style={brutalistStyles.brutalistInput}
                placeholder="Mínimo 8 caracteres"
                required
                minLength={8}
              />
              {errors.password && (
                <span style={brutalistStyles.brutalistError}>{errors.password}</span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label htmlFor="confirmPassword" style={brutalistStyles.brutalistLabel}>
                Confirmar Contraseña
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                style={brutalistStyles.brutalistInput}
                placeholder="Repite la contraseña"
                required
                minLength={8}
              />
              {errors.confirmPassword && (
                <span style={brutalistStyles.brutalistError}>{errors.confirmPassword}</span>
              )}
            </div>

            <div style={{ marginTop: '1rem' }}>
              <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#aaa' : '#666' }}>
                La contraseña debe contener:
              </p>
              <ul style={{ 
                fontSize: '0.9rem', 
                color: isDarkMode ? '#aaa' : '#666',
                marginLeft: '1.5rem',
                marginTop: '0.5rem'
              }}>
                <li>Al menos 8 caracteres</li>
                <li>Una letra mayúscula</li>
                <li>Una letra minúscula</li>
                <li>Un número</li>
                <li>Un carácter especial (@$!%*?&)</li>
              </ul>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <BrutalistNavigation />
      <div className="container" style={{
        ...brutalistStyles.formContainer,
        marginTop: isMobile ? '6rem' : '4rem'
      }}>
        <h1 style={brutalistStyles.brutalistTitle}>
          REGISTRAR NUEVA AUTOESCUELA
        </h1>
        
        <FormStepper steps={STEPS} currentStep={currentStep} />

        {errors.submit && (
          <div style={brutalistStyles.brutalistError}>
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          maxWidth: '600px',
          margin: '0 auto',
          width: '100%'
        }}>
          {renderStepContent()}

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            {currentStep > 0 && (
              <button
                type="button"
                onClick={handleBack}
                style={{
                  ...brutalistStyles.brutalistButton,
                  backgroundColor: '#6c757d',
                  ...(isLoading && brutalistStyles.disabledState)
                }}
                disabled={isLoading}
              >
                Anterior
              </button>
            )}

            {currentStep < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={handleNext}
                style={{
                  ...brutalistStyles.brutalistButton,
                  ...(isLoading && brutalistStyles.disabledState)
                }}
                disabled={isLoading}
              >
                Siguiente
              </button>
            ) : (
              <button
                type="submit"
                style={{
                  ...brutalistStyles.brutalistButtonSecondary,
                  ...(isLoading && brutalistStyles.disabledState)
                }}
                disabled={isLoading}
              >
                {isLoading ? 'Registrando...' : 'Completar Registro'}
              </button>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default RegistrarAutoescuela;